import {
  afterNextRender,
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  Input,
  numberAttribute,
  OnChanges,
  Output,
  SimpleChanges,
  signal,
  untracked,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import {
  type RagAskConfig,
  type RagAskLabels,
  type RagAskPosition,
  type RagAskStreamMode,
  type RagAskTransport,
  type RagAskTheme,
} from '../config/rag-ask.config';
import type { AskSource, ChatMessage, RagCollectionRef } from '../models/ask.models';
import { RagMarkdownPipe } from '../pipes/markdown.pipe';
import { RagAskService } from '../services/rag-ask.service';
import { RagPreviewService } from '../services/rag-preview.service';
import {
  defaultEstimate,
  VIRTUAL_THRESHOLD,
  VirtualListState,
  type VirtualWindow,
} from '../util/virtual-list';
import { RagAskMessageComponent, type DownloadRequest } from './rag-ask-message.component';
import { RagIconComponent } from './rag-icon.component';

/** Payload of the `answer` output / DOM event. */
export interface RagAnswerEvent {
  readonly question: string;
  readonly answer: string;
  readonly sources: readonly AskSource[];
}

/**
 * The embeddable RAG ask widget: a floating launcher that opens a chat panel.
 *
 * `ViewEncapsulation.ShadowDom` gives real style isolation — the host page
 * cannot bleed into the widget and the widget cannot bleed out. Every child
 * component of the widget therefore declares no styles of its own: their DOM
 * lives inside this shadow root, so this single stylesheet covers them.
 */
@Component({
  selector: 'assistant-rag',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
  providers: [RagAskService, RagPreviewService],
  imports: [NgIf, NgFor, RagIconComponent, RagAskMessageComponent, RagMarkdownPipe],
  host: {
    '[attr.data-rag-theme]': 'theme()',
    '[attr.data-rag-position]': 'position()',
    '[attr.data-rag-inline]': 'inline() ? "" : null',
    '[style.--rag-accent]': 'accentColor()',
    '[style.--rag-offset]': 'offsetPx()',
    '[style.--rag-panel-w]': 'panelWidthPx()',
    '[style.--rag-panel-h]': 'panelHeightPx()',
    '[style.z-index]': 'inline() ? null : zIndex()',
    '(keydown.escape)': 'onEscape()',
    '(window:resize)': 'onViewportResize()',
  },
  templateUrl: './rag-ask-widget.component.html',
  styleUrls: ['./rag-ask-widget.component.scss'],
})
export class RagAskWidgetComponent implements OnChanges {
  private readonly ask = inject(RagAskService);
  protected readonly preview = inject(RagPreviewService);
  private readonly injector = inject(Injector);
  private readonly cdr = inject(ChangeDetectorRef);

  // ── Inputs ─────────────────────────────────────────────────────────────────
  // Every option is also reachable through `config`, which is what a
  // framework-agnostic host sets as a property. The individual inputs exist so
  // the custom element can be driven by plain HTML attributes.

  // ── Inputs (Angular 16 @Input — configure in the host template) ───────────
  @Input() config: Partial<RagAskConfig> | string | undefined;
  @Input() apiUrl: string | undefined;
  @Input() askPath: string | undefined;
  @Input() collection: string | number | null | undefined;
  @Input() collections: RagCollectionRef[] | string | undefined;
  @Input() headers: RagAskConfig['headers'] | string | undefined;
  @Input() extraBody: Record<string, unknown> | string | undefined;
  @Input() labels: Partial<RagAskLabels> | string | undefined;
  @Input() streamMode: RagAskStreamMode | undefined;
  @Input() transport: RagAskTransport | undefined;
  @Input() noAnswerText: string | undefined;
  @Input() storageKey: string | undefined;
  @Input() persist: 'none' | 'session' | 'local' | undefined;
  @Input() locale: string | undefined;
  @Input() downloadLinkMarker: string | false | undefined;
  @Input({ transform: optionalBoolean }) withCredentials: boolean | undefined;
  @Input() useHistory: boolean | undefined;
  @Input() showScores: boolean | undefined;
  @Input() showSources: boolean | undefined;
  @Input() showFreshness: boolean | undefined;
  @Input() maxHistoryTurns: number | undefined;
  @Input() minChars: number | undefined;
  @Input() maxChars: number | undefined;
  @Input() typewriter: boolean | number | undefined;
  @Input('position') positionInput: RagAskPosition | undefined;
  @Input('theme') themeInput: RagAskTheme | undefined;
  @Input('accentColor') accentColorInput: string | undefined;
  @Input('offset') offsetInput: number | undefined;
  @Input('zIndex') zIndexInput: number | undefined;
  @Input('panelWidth') panelWidthInput: number | undefined;
  @Input('panelHeight') panelHeightInput: number | undefined;
  @Input('inline') inlineInput: boolean | undefined;
  @Input() startOpen: boolean | undefined;

  // ── Outputs ────────────────────────────────────────────────────────────────

  @Output() readonly opened = new EventEmitter<void>();
  @Output() readonly closed = new EventEmitter<void>();
  @Output() readonly question = new EventEmitter<string>();
  @Output() readonly answer = new EventEmitter<RagAnswerEvent>();
  @Output() readonly failure = new EventEmitter<string>();

  // ── State ──────────────────────────────────────────────────────────────────

  @ViewChild('thread') private threadEl?: ElementRef<HTMLElement>;
  @ViewChild('input') private inputEl?: ElementRef<HTMLTextAreaElement>;
  /**
   * The popover sits under `*ngIf`, so it is registered with the service each
   * time it is created or destroyed. (An `effect` reading a plain ViewChild
   * runs only once, before the popover exists.)
   */
  @ViewChild('popover') protected set popoverRef(ref: ElementRef<HTMLElement> | undefined) {
    this.preview.registerPopover(ref?.nativeElement ?? null);
  }

  protected readonly draft = signal('');
  protected readonly focused = signal(false);
  protected readonly isOpen = signal(false);
  protected readonly unread = signal(0);
  protected readonly scopedCollection = signal<string | number | null>(null);

  protected readonly messages = this.ask.messages;
  protected readonly isStreaming = this.ask.isStreaming;
  protected readonly hasConversation = this.ask.hasConversation;

  // Virtual list — only active once the thread exceeds VIRTUAL_THRESHOLD.
  private readonly virtual = new VirtualListState<ChatMessage>(defaultEstimate);
  private readonly scrollTop = signal(0);
  private readonly viewportHeight = signal(0);
  /** True while the reader is near the bottom (auto-follow streaming text). */
  private pinnedToBottom = true;
  private viewportObserver: ResizeObserver | null = null;
  private measureRaf: number | null = null;

  /**
   * Slice of messages to render. Short threads pass through unchanged; long
   * threads return only the overscan window plus top/bottom spacers.
   */
  protected readonly virtualWindow = computed<VirtualWindow<ChatMessage>>(() => {
    const msgs = this.messages();
    if (msgs.length < VIRTUAL_THRESHOLD) {
      return {
        items: msgs,
        startIndex: 0,
        padTop: 0,
        padBottom: 0,
        totalHeight: 0,
      };
    }
    // Depend on scroll signals so the window recomputes on scroll/resize.
    const top = this.scrollTop();
    const height = this.viewportHeight();
    const forceTail = this.isStreaming() && this.pinnedToBottom;
    return this.virtual.window(msgs, top, height || 400, forceTail);
  });

  protected readonly useVirtual = computed(() => this.messages().length >= VIRTUAL_THRESHOLD);

  /** Effective configuration: defaults ← `provideAssistantRag` ← inputs. */
  protected readonly cfg = this.ask.config;
  protected readonly text = computed(() => this.cfg().labels);

  protected readonly theme = computed(() => this.cfg().theme);
  protected readonly position = computed(() => this.cfg().position);
  protected readonly accentColor = computed(() => this.cfg().accentColor);
  protected readonly zIndex = computed(() => this.cfg().zIndex);
  protected readonly inline = computed(() => this.cfg().inline);
  protected readonly collectionList = computed(() => this.cfg().collections);

  protected readonly offsetPx = computed(() => `${this.cfg().offset}px`);
  protected readonly panelWidthPx = computed(() => `${this.cfg().panelWidth}px`);
  protected readonly panelHeightPx = computed(() => `${this.cfg().panelHeight}px`);

  /** Current scope as a `<select>` value (always a string). */
  protected readonly scopeValue = computed(() => {
    const value = this.scopedCollection();
    return value === null || value === undefined ? '' : String(value);
  });

  protected readonly panelVisible = computed(() => this.inline() || this.isOpen());

  protected readonly remaining = computed(() => this.cfg().maxChars - this.draft().length);
  protected readonly showCounter = computed(() => this.remaining() <= 200);

  protected readonly canSend = computed(
    () => this.draft().trim().length >= this.cfg().minChars && this.draft().length <= this.cfg().maxChars,
  );

  /** One override object pushed into the service whenever an input changes. */
  private buildOverrides(): Partial<RagAskConfig> {
    const fromConfig: Partial<RagAskConfig> = coercePartialConfig(this.config) ?? {};
    const fromInputs = definedOnly<RagAskConfig>({
      apiUrl: this.apiUrl,
      askPath: this.askPath,
      collection: this.collection,
      collections: coerceJson(this.collections),
      headers: coerceHeaders(this.headers),
      extraBody: coerceJson(this.extraBody),
      labels: coerceJson(this.labels),
      streamMode: this.streamMode,
      transport: this.transport,
      noAnswerText: this.noAnswerText,
      storageKey: this.storageKey,
      persist: this.persist,
      locale: this.locale,
      downloadLinkMarker: this.downloadLinkMarker,
      withCredentials: this.withCredentials,
      history: this.useHistory,
      showScores: this.showScores,
      showSources: this.showSources,
      showFreshness: this.showFreshness,
      maxHistoryTurns: this.maxHistoryTurns,
      minChars: this.minChars,
      maxChars: this.maxChars,
      typewriter: this.typewriter,
      position: this.positionInput,
      theme: this.themeInput,
      accentColor: this.accentColorInput,
      offset: this.offsetInput,
      zIndex: this.zIndexInput,
      panelWidth: this.panelWidthInput,
      panelHeight: this.panelHeightInput,
      inline: this.inlineInput,
      startOpen: this.startOpen,
    });

    // `labels` must merge rather than replace, so the two layers are combined.
    const labels = { ...(fromConfig.labels ?? {}), ...(fromInputs.labels ?? {}) };
    const merged: Partial<RagAskConfig> = { ...fromConfig, ...fromInputs };
    if (Object.keys(labels).length) merged.labels = labels;
    return merged;
  }


  private readonly emittedStatus = new Map<string, ChatMessage['status']>();
  private pendingQuestion = '';
  private startOpenApplied = false;
  private lastConfigCollection: string | number | null | undefined;

  ngOnChanges(_changes: SimpleChanges): void {
    // Recompute overrides whenever any @Input changes (Angular 16).
    this.syncConfigFromInputs();
  }

  private syncConfigFromInputs(): void {
    const overrides = this.buildOverrides();
    this.ask.setConfig(overrides);
  }

  constructor() {

    // Config is pushed from ngOnChanges via syncConfigFromInputs (inputs are not signals).

    // Honour `startOpen` once, so a later config change cannot re-open a panel
    // the user has deliberately closed.
    effect(() => {
      const start = this.cfg().startOpen;
      untracked(() => {
        if (start && !this.startOpenApplied) {
          this.startOpenApplied = true;
          this.isOpen.set(true);
        }
      });
    });

    // Follow the configured collection, but never overwrite a choice the user
    // made in the picker.
    effect(() => {
      const collection = this.cfg().collection;
      untracked(() => {
        if (collection === this.lastConfigCollection) return;
        this.lastConfigCollection = collection;
        this.scopedCollection.set(collection);
      });
    });

    // Keep the thread pinned to the latest turn, unless the reader has
    // scrolled up to re-read something. Works with both the full list and the
    // virtual window (spacers make scrollHeight reflect total height).
    effect(() => {
      this.messages();
      this.virtualWindow();
      const el = untracked(() => this.threadEl?.nativeElement);
      if (!el || !this.pinnedToBottom) return;
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
        this.scrollTop.set(el.scrollTop);
      });
    });

    // Prune height cache when messages are removed (reset / retry).
    effect(() => {
      const msgs = this.messages();
      untracked(() => {
        if (msgs.length === 0) {
          this.virtual.clear();
          this.pinnedToBottom = true;
          return;
        }
        this.virtual.prune(new Set(msgs.map((m) => m.id)));
      });
    });

    // Attach observers once the thread element is in the DOM.
    effect(() => {
      const el = this.threadEl?.nativeElement ?? null;
      untracked(() => this.bindThreadObservers(el));
    });

    // Re-measure rows after the virtual window changes (new messages, scroll).
    effect(() => {
      this.virtualWindow();
      untracked(() => this.measureRows());
    });

    // Surface completed answers and failures to the host application.
    effect(() => {
      const messages = this.messages();
      untracked(() => this.emitTransitions(messages));
    });
  }

  // ── Panel ──────────────────────────────────────────────────────────────────

  protected toggle(): void {
    this.isOpen() ? this.close() : this.open();
  }

  open(): void {
    if (this.isOpen()) return;
    this.isOpen.set(true);
    this.unread.set(0);
    this.opened.emit();
    requestAnimationFrame(() => this.inputEl?.nativeElement.focus());
  }

  close(): void {
    if (!this.isOpen()) return;
    this.preview.hide();
    this.isOpen.set(false);
    this.closed.emit();
  }

  /** Keep an open extract popover inside the resized panel. */
  protected onViewportResize(): void {
    this.preview.reposition();
  }

  protected onEscape(): void {
    if (this.preview.entries()) {
      this.preview.hide();
      return;
    }
    if (!this.inline()) this.close();
  }

  // ── Composer ───────────────────────────────────────────────────────────────

  protected onInput(event: Event): void {
    const el = event.target as HTMLTextAreaElement;
    this.draft.set(el.value);
    this.autoResize(el);
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
    event.preventDefault();
    this.submit();
  }

  /** Ask the current draft, or stop generation when one is in flight. */
  submit(): void {
    if (this.isStreaming()) {
      this.ask.stop();
      return;
    }
    const text = this.draft().trim();
    if (!this.canSend()) return;

    this.pendingQuestion = text;
    this.question.emit(text);
    this.draft.set('');

    const el = this.inputEl?.nativeElement;
    if (el) {
      el.value = '';
      el.style.height = 'auto';
    }

    void this.ask.send(text, this.scopedCollection());
  }

  /** Programmatic entry point — `element.askQuestion('…')` from a host page. */
  askQuestion(text: string): void {
    this.open();
    this.draft.set(text);
    this.submit();
  }

  protected resetThread(): void {
    this.preview.hide();
    this.ask.reset();
    this.emittedStatus.clear();
    this.virtual.clear();
    this.pinnedToBottom = true;
    this.scrollTop.set(0);
    requestAnimationFrame(() => this.inputEl?.nativeElement.focus());
  }

  protected onCollectionChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.scopedCollection.set(value === '' ? null : matchCollectionId(this.collectionList(), value));
  }

  protected isScoped(id: string | number): boolean {
    return String(id) === this.scopeValue();
  }

  protected clearScope(): void {
    this.scopedCollection.set(null);
  }

  protected onDownload(request: DownloadRequest): void {
    void this.ask.downloadFile(request.url, request.filename);
  }

  protected trackById(_index: number, message: ChatMessage): string {
    return message.id;
  }

  protected trackByCollection(_index: number, item: RagCollectionRef): string | number {
    return item.id;
  }

  protected trackByEntry(_index: number, entry: { rank: number }): number {
    return entry.rank;
  }

  // ── Virtual list ───────────────────────────────────────────────────────────

  /** Bound to the thread's scroll event — drives the visible window. */
  protected onThreadScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const top = el.scrollTop;
    const distanceFromBottom = el.scrollHeight - top - el.clientHeight;
    this.pinnedToBottom = distanceFromBottom < 140;
    this.scrollTop.set(top);
    this.viewportHeight.set(el.clientHeight);
    // Keep an open extract popover attached to its trigger.
    this.preview.reposition();
  }

  /**
   * After each render of the virtual window, measure row heights so estimates
   * converge to real sizes. Passes the message into setHeight so the per-bucket
   * running average can self-calibrate to the host's font metrics.
   */
  protected measureRows(): void {
    if (this.measureRaf !== null) return;
    this.measureRaf = requestAnimationFrame(() => {
      this.measureRaf = null;
      const el = this.threadEl?.nativeElement;
      if (!el) return;

      const byId = new Map(this.messages().map((m) => [m.id, m] as const));
      let changed = false;
      el.querySelectorAll<HTMLElement>('[data-rag-msg-id]').forEach((node) => {
        const id = node.getAttribute('data-rag-msg-id');
        if (!id) return;
        if (this.virtual.setHeight(id, node.offsetHeight, byId.get(id))) changed = true;
      });

      if (changed) {
        // Force a window recompute with updated heights.
        this.scrollTop.update((v) => v);
        if (this.pinnedToBottom) {
          el.scrollTop = el.scrollHeight;
          this.scrollTop.set(el.scrollTop);
        }
      }
    });
  }

  private bindThreadObservers(el: HTMLElement | null): void {
    this.viewportObserver?.disconnect();
    this.viewportObserver = null;
    if (!el) return;

    this.viewportHeight.set(el.clientHeight);
    this.scrollTop.set(el.scrollTop);

    if (typeof ResizeObserver === 'function') {
      this.viewportObserver = new ResizeObserver(() => {
        this.viewportHeight.set(el.clientHeight);
        if (this.pinnedToBottom) {
          el.scrollTop = el.scrollHeight;
          this.scrollTop.set(el.scrollTop);
        }
      });
      this.viewportObserver.observe(el);
    }

    // Initial measurement after the first paint of messages.
    afterNextRender(() => this.measureRows(), { injector: this.injector });
  }

  // ── Internals ──────────────────────────────────────────────────────────────

  private autoResize(el: HTMLTextAreaElement): void {
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }

  private emitTransitions(messages: readonly ChatMessage[]): void {
    for (const message of messages) {
      if (message.role === 'user') continue;
      const previous = this.emittedStatus.get(message.id);
      if (previous === message.status) continue;
      this.emittedStatus.set(message.id, message.status);

      if (previous === 'streaming' && message.status === 'done') {
        this.answer.emit({
          question: this.pendingQuestion,
          answer: message.content,
          sources: message.sources ?? [],
        });
        // Badge the launcher only for answers that landed during this session
        // while the panel was closed — never for a restored conversation.
        if (!this.isOpen() && !this.inline()) this.unread.update((n) => n + 1);
      } else if (message.status === 'error') {
        this.failure.emit(message.content);
      }
    }
  }
}

// ─── Attribute coercion ───────────────────────────────────────────────────────
// Custom-element attributes arrive as strings; these keep `undefined` meaning
// "not set" so DI-provided config is not clobbered by unset inputs.

function optionalBoolean(value: unknown): boolean | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'string' && (value === 'false' || value === '0')) return false;
  return booleanAttribute(value);
}

function optionalNumber(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const parsed = numberAttribute(value, Number.NaN);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * `collection="12"` arrives as a string from an attribute. A numeric id is
 * converted back to a number so the request body matches what the API expects;
 * non-numeric ids are passed through untouched.
 */
function collectionAttribute(
  value: string | number | null | undefined,
): string | number | null | undefined {
  if (typeof value !== 'string') return value;
  if (value === '' ) return null;
  return /^\d+$/.test(value) ? Number(value) : value;
}

function typewriterAttribute(value: unknown): boolean | number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value;
  if (value === 'false' || value === 'off') return false;
  if (value === 'true' || value === 'on') return true;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}


/** Like {@link jsonAttribute}, but also keeps function suppliers intact. */

function coercePartialConfig(value: Partial<RagAskConfig> | string | undefined): Partial<RagAskConfig> | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'string') return jsonAttribute<Partial<RagAskConfig>>(value);
  return value;
}

function coerceJson<T>(value: T | string | undefined): T | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'string') return jsonAttribute<T>(value);
  return value;
}

function coerceHeaders(value: RagAskConfig['headers'] | string | undefined): RagAskConfig['headers'] | undefined {
  return headersAttribute(value as any);
}

function headersAttribute(
  value: RagAskConfig['headers'] | string | undefined,
): RagAskConfig['headers'] | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'function') return value;
  if (typeof value === 'object') return value;
  return jsonAttribute<Record<string, string>>(value);
}

/** Accept either a real object or a JSON string (attribute form). */
function jsonAttribute<T>(value: T | string | undefined): T | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value) as T;
  } catch {
    console.warn('[rag-ask] Ignoring attribute: not valid JSON.', value);
    return undefined;
  }
}

function definedOnly<T extends object>(source: Record<string, unknown>): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(source)) {
    if (value !== undefined) out[key] = value;
  }
  return out as Partial<T>;
}

/** `<select>` values are strings; restore the original id type. */
function matchCollectionId(
  collections: readonly RagCollectionRef[],
  value: string,
): string | number | null {
  const found = collections.find((c) => String(c.id) === value);
  return found ? found.id : value;
}
